import {
  AccountCategory,
  BaseCurrency,
  CashInfo,
  Currency,
  getFormatter,
  InstrumentCategoryEnum,
  isDefined,
  ListedInstrument,
  Loadable,
  LoadableState,
} from '@eih-netbroker/common';
import { Fragment, useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { MoneyMaskHOC } from '../../../../../shared/components/money-mask/MoneyMask';
import { useAccountBalance } from '../../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../../shared/services/useAccounts';
import { useTradingClassification } from '../../../../../shared/services/useTradingClassification';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { useLogger } from '../../../../../shared/utils/useLogger';
import { useCollateralSummary } from '../../../../portfolio/services/useCollateralSummary';
import { useOrderManagement } from '../../../services/order/useOrderManagement';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import { OrderTransactionInfoProps } from './OrderTransactionInfo';
import classes from './OrderTransactionInfo.module.scss';

const unknownValue = '-';

export const OrderTransactionInfoOrderBuyInfo = ({ order, sourceComponent }: OrderTransactionInfoProps) => {
  const logger = useLogger(`OrderTransactionInfo`);
  const { selectedAccountOrClient } = useAccounts();
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();
  const { getCashInfoByCurrency } = useAccountBalance();
  const cashInfoByCurrency = getCashInfoByCurrency(selectedAccountOrClient);
  const { tradingClassification } = useTradingClassification();
  const { getMaxOpenPosition } = useOrderManagement();
  const { loadCollateralSummary, activeCollateralSummary, collateralSummary } =
    useCollateralSummary(selectedAccountOrClient);

  // If uninitialized, do not display max position row
  const [maxPosition, setMaxPosition] = useState<LoadableState<number>>(Loadable.uninitialized);

  const calculateNonFuturesMaxPosition = useCallback(
    async (instrument: ListedInstrument) => {
      try {
        const maxOpenPosition = await getMaxOpenPosition(instrument);
        setMaxPosition(Loadable.loaded(maxOpenPosition.data.maximumPosition));
      } catch (err) {
        setMaxPosition(Loadable.failed);
        logger.error('Failed to get max open position', err);
      }
    },
    [logger, getMaxOpenPosition],
  );

  const initFuturesMaxPosition = useEffectEvent(async () => {
    if (order.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
      setMaxPosition(Loadable.loading);
      if (Loadable.isUninitialized(collateralSummary)) {
        await loadCollateralSummary();
      }

      if (collateralSummary.isLoading) {
        return;
      }

      if (!collateralSummary.isLoaded) {
        setMaxPosition(Loadable.failed);
        return;
      }

      if (collateralSummary.isLoaded) {
        const marginRequirementMax =
          collateralSummary.collateralSummaryAmountsByClientId.totalCollateralValue -
          collateralSummary.collateralSummaryAmountsByClientId.marginRequirement;

        if (selectedAccountOrClient.account?.accountCategory === AccountCategory.Normal) {
          setMaxPosition(Loadable.loaded(marginRequirementMax));
        } else {
          if (!activeCollateralSummary) {
            setMaxPosition(Loadable.failed);
            return;
          }

          setMaxPosition(
            Loadable.loaded(
              Math.min(
                marginRequirementMax,
                activeCollateralSummary.totalCollateralValue - activeCollateralSummary.marginRequirement,
              ),
            ),
          );
        }
      }
    }
  });

  const initNonFuturesMaxPosition = useEffectEvent(() => {
    if (
      order.instrument?.instrumentCategory !== InstrumentCategoryEnum.Futures &&
      tradingClassification?.hasInvestmentLoanContract &&
      isDefined(order.instrument?.collateralValue) &&
      order.instrument.collateralValue < 1 &&
      order.instrument.collateralValue > 0
    ) {
      setMaxPosition(Loadable.loading);
      calculateNonFuturesMaxPosition(order.instrument);
    }
  });

  useEffect(() => {
    setMaxPosition(Loadable.uninitialized);
    initFuturesMaxPosition();
    initNonFuturesMaxPosition();
  }, [order.instrument]);

  // For futures, max position is determined by collateral summary, so it needs to be re-calculated when collateral summary changes / loads in
  useEffect(() => {
    initFuturesMaxPosition();
  }, [collateralSummary]);

  const foreignCurrencies = useMemo<LoadableState<Array<[Currency, CashInfo]>>>(() => {
    if (!isDefined(order.instrument)) {
      return Loadable.uninitialized;
    }

    if (cashInfoByCurrency.isLoading) {
      return Loadable.loading;
    }

    if (cashInfoByCurrency.isLoaded) {
      return Loadable.loaded(
        (Object.entries(cashInfoByCurrency.cashInfo) as Array<[Currency, CashInfo]>).filter(
          ([ccy, info]) => ccy !== order.instrument?.currency && isDefined(info?.investable) && info.investable > 0,
        ),
      );
    }

    return Loadable.uninitialized;
  }, [cashInfoByCurrency, order.instrument]);

  return (
    <>
      <div className={cssWhen(classes.row, [Loadable.isUninitialized(maxPosition), classes.extraTopPadding])}>
        <span
          className={classes.label}
          data-testid="order-transaction-info-currency-balance-label"
        >
          {translation.orderModal.transactionInfo.currencyBalance}
        </span>
        <MoneyMaskHOC
          isInverse
          size={100}
          bypass={sourceComponent === OrderFormSourceComponent.Modal}
        >
          <NbIndefiniteLoaderValueWrapper
            loadableValue={foreignCurrencies}
            loadedValueCb={loadedValue => (
              <div data-testid="order-transaction-info-currency-balance-value">
                {loadedValue.length > 0
                  ? loadedValue.map(([ccy, info], ind) => (
                      <Fragment key={ccy}>
                        <span
                          className={classes.valueSecondary}
                          data-testid={`order-transaction-info-currency-balance-value-${ccy}`}
                        >
                          {formatCurrency(info.investable, ccy)}
                        </span>
                        {ind < loadedValue.length - 1 && <span className={classes.dotDivider}>{' • '}</span>}
                      </Fragment>
                    ))
                  : unknownValue}
              </div>
            )}
          />
        </MoneyMaskHOC>
      </div>
      {!Loadable.isUninitialized(maxPosition) && (
        <div className={classes.row}>
          <span
            className={classes.label}
            data-testid="order-transaction-info-max-position-label"
          >
            {translation.orderModal.transactionInfo.maxPosition}
          </span>
          <MoneyMaskHOC
            isInverse
            size={100}
            bypass={sourceComponent === OrderFormSourceComponent.Modal}
          >
            <NbIndefiniteLoaderValueWrapper
              loadableValue={maxPosition}
              loadedValueCb={loadedValue => (
                <span
                  className={classes.value}
                  data-testid="order-transaction-info-max-position-value"
                >
                  {formatCurrency(
                    loadedValue.value,
                    order.instrument?.currency ?? BaseCurrency,
                    order.instrument?.currency === BaseCurrency,
                  )}
                </span>
              )}
            />
          </MoneyMaskHOC>
        </div>
      )}
    </>
  );
};
